import moment from 'moment';
import { useEffect, useMemo, useState, useContext } from 'react';
import { Button } from '@mantine/core';
import MantineTable from '../../../../../common/table/mantine';
import { axios } from '../../../../../utils/axios';
import { useParams } from 'react-router-dom';
import { Status } from '../../../styles';
import { showAlert } from '../../../../../utils/alerts';
import { CostingRequestsContext } from '../../../../../contexts/CostingRequest';
import AssignedUsersList from '../../../../../components/assignedUsersList';

export const CreateTPFlow = ({ setLogsModal, activeTab, activeSubTab }) => {
  const { crNumber } = useParams();
  const [loading, setLoading] = useState(false);
  const [skus, setSkus] = useState([]);
  const { isCrRoleAuthorized, info, crAssignedSpocs } = useContext(CostingRequestsContext);
  const { producing_plant_data } = info;

  useEffect(() => {
    if (
      activeTab === 'tpSetup' &&
      activeSubTab === 'createTpFlow' &&
      producing_plant_data.length > 0
    ) {
      fetch();
    }
  }, [activeTab, activeSubTab, producing_plant_data]);

  const fetch = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/costing-request/step/tp_flow/${crNumber}`);
      const responseData = response.data.data;
      const data = responseData.IntCrTpRecords.map((tpRecord) => {
        return {
          sku_number: tpRecord?.sku,
          sku_description: tpRecord?.sku_description,
          producing_plant: responseData?.int_cr_producing_plant.producing_plant,
          producing_country: tpRecord?.producing_country,
          buying_country: tpRecord?.buying_country,
          avic_hl: tpRecord?.cost_per_hl
            ? parseFloat(tpRecord?.cost_per_hl || 0).toFixed(2)
            : 'N/A',
          indicativeDb: tpRecord.indicative_database,
          status: tpRecord?.status,
          logs: tpRecord?.logs,
          assignedTo: crAssignedSpocs(producing_plant_data[0].producing_country),
          spoc: responseData?.costing_ticket_assigned?.name || 'N/A',
          step_id: responseData?.step_id,
          sku_id: responseData?.sku_id,
          producing_plant_id: responseData?.producing_plant_id,
          row_id: tpRecord?.id,
          user_email: tpRecord?.user_email,
        };
      });
      setSkus(data);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async (row_id) => {
    try {
      setLoading(true);
      const payload = {
        row_id: row_id,
      };

      const response = await axios.post(`/costing-request/step/tp_flow_retry/${crNumber}`, payload);

      if (response.status === 200) {
        showAlert(
          {
            message: 'TP Flow submitted successfully in Anaplan',
          },
          'success',
        );
      } else {
        throw new Error(response.data.message || 'Failed to refresh costing request');
      }
      await fetch();
    } catch (error) {
      showAlert(
        {
          message: 'TP Submission failed in Anaplan. Please check logs',
        },
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'action',
        header: 'Action',
        Cell: ({ row }) => {
          const status = row.original?.status;
          const rowId = row.original?.row_id;
          const hasRefreshAccess = isCrRoleAuthorized(producing_plant_data[0].producing_country);

          const isDisabled = !(
            hasRefreshAccess && String(status).toLowerCase().includes('progress')
          );
          return (
            <Button
              variant='subtle'
              color='dark'
              size='sm'
              style={{ width: '100%' }}
              onClick={() => handleRefresh(rowId)}
              disabled={isDisabled}
            >
              Retry
            </Button>
          );
        },
      },
      {
        accessorKey: 'sku_number',
        header: 'SKU No',
      },
      {
        accessorKey: 'sku_description',
        header: 'SKU Desc',
      },
      {
        accessorKey: 'producing_country',
        header: 'Producing Country',
      },
      {
        accessorKey: 'buying_country',
        header: 'Buying Country',
      },
      {
        accessorKey: 'avic_hl',
        header: 'AVIC/HL',
      },
      {
        accessorKey: 'indicativeDb',
        header: 'Indicative DB',
      },
      {
        id: 'status',
        header: 'Status',
        Cell: ({ row }) => <Status status={row.original.status} />,
      },
      {
        id: 'logs',
        header: 'Logs',
        Cell: ({ row }) => {
          const { user_email, logs } = row.original;
          const updateLogs = logs.map((log) => ({
            ...log,
            user_email: user_email,
          }));

          return (
            <Button
              color='dark'
              onClick={() =>
                setLogsModal({
                  open: true,
                  logs: updateLogs,
                  type: 'Create TP Flow Check',
                })
              }
            >
              View Logs
            </Button>
          );
        },
      },
      {
        accessorKey: 'assignedTo',
        header: 'FP&A GCC SME',
        Cell: ({
          row: {
            original: { assignedTo },
          },
        }) => <AssignedUsersList users={assignedTo} />,
      },
    ],
    [skus],
  );
  return (
    <>
      <MantineTable
        columns={columns}
        initialData={skus}
        unique='sku'
        enableRowSelection={false}
        loading={loading}
        showSelectedToggle={false}
        hideSelectColumn={true}
        showResetAll={false}
        enableExport={true}
        csvFilename={`create_tpflow_check_${moment().format('YYYY-MM-DD')}`}
        pageSize={10}
      />
    </>
  );
};
